import { useCallback, useEffect, useRef, useState } from "react"
import type { OpenCodeApi } from "../../lib/opencodeApi"
import type { Question, QuestionInfo } from "../../lib/opencodeTypes"
import { startSingleFlightPoll } from "../../lib/singleFlightPoll"
import {
  allowsCustom,
  answerFor,
  buildReply,
  chooseCustom,
  emptyState,
  isAnswered,
  isComplete,
  isSingleStep,
  setCustom,
  toggleOption,
  type QuestionState,
} from "../../lib/questions"
import { Button } from "../ui"
import { AgentDialog } from "./AgentDialog"

// The agent's question prompt.
//
// One question at a time, which is what opencode's own clients do
// (packages/tui/src/routes/session/question.tsx and packages/app's question
// dock). This used to render every pending request, every sub-question and
// every option at once, in a shell that could not scroll — so a set of four
// questions put Submit off the bottom of the screen.
//
// A single single-select question gets no stepper at all: clicking an option
// answers it. Chrome for a decision with one step is chrome for nothing.

const CUSTOM_LABEL = "Type your own answer"

interface QuestionDialogProps {
  api: OpenCodeApi
  sessionId: string
}

export function QuestionDialog({ api, sessionId }: QuestionDialogProps) {
  const [questions, setQuestions] = useState<Question[]>([])

  useEffect(() => {
    return startSingleFlightPoll(async (signal) => {
      try {
        setQuestions(await api.listQuestions(sessionId, signal))
      } catch (err) {
        if (!signal.aborted) console.error("Failed to load questions:", err)
      }
    }, 2000)
  }, [api, sessionId])

  // One request at a time. Several can be pending, and stacking them was half
  // of what made this dialog too tall; the next one appears once this is gone.
  const request = questions[0]
  if (!request) return null

  return (
    <QuestionCard
      // Keyed by request id so the answer state cannot leak from one request to
      // the next — every slot is positional, and carrying them over would send
      // answers to questions nobody read.
      key={request.id}
      request={request}
      onReply={async (answers) => {
        await api.replyToQuestion(sessionId, request.id, answers)
        setQuestions((prev) => prev.filter((q) => q.id !== request.id))
      }}
      onReject={async () => {
        await api.rejectQuestion(sessionId, request.id)
        setQuestions((prev) => prev.filter((q) => q.id !== request.id))
      }}
    />
  )
}

function QuestionCard({
  request,
  onReply,
  onReject,
}: {
  request: Question
  onReply: (answers: string[][]) => Promise<void>
  onReject: () => Promise<void>
}) {
  const items = request.questions
  const single = isSingleStep(items)

  const [step, setStep] = useState(0)
  const [state, setState] = useState<QuestionState>(() => emptyState(items.length))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const customRef = useRef<HTMLTextAreaElement>(null)

  const current = items[step]
  const last = step >= items.length - 1

  const submit = useCallback(
    async (answers: string[][]) => {
      setBusy(true)
      setError(null)
      try {
        await onReply(answers)
      } catch (err) {
        // Shown next to the button that caused it. Swallowing this into the
        // console is how the dialog once spent a while looking dead.
        setError(err instanceof Error ? err.message : "Failed to send answer")
        setBusy(false)
      }
    },
    [onReply],
  )

  const dismiss = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await onReject()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to dismiss")
      setBusy(false)
    }
  }, [onReject])

  const next = useCallback(() => {
    if (busy) return
    if (last) {
      if (!isComplete(items, state)) {
        setError("Answer every question before submitting")
        return
      }
      void submit(buildReply(items, state))
      return
    }
    setError(null)
    setStep((s) => s + 1)
  }, [busy, last, items, state, submit])

  const back = useCallback(() => {
    if (busy || step === 0) return
    setError(null)
    setStep((s) => s - 1)
  }, [busy, step])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy) return
      if (e.key === "Escape") {
        e.preventDefault()
        void dismiss()
        return
      }
      // Arrows only when not typing — they belong to the textarea's caret then.
      const typing = document.activeElement?.tagName === "TEXTAREA"
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        next()
        return
      }
      if (typing || single) return
      if (e.key === "ArrowRight") {
        e.preventDefault()
        next()
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        back()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [busy, single, next, back, dismiss])

  if (!current) return null

  const pick = (label: string) => {
    const updated = toggleOption(state, step, label, current.multiple === true)
    setState(updated)
    setError(null)
    // One single-select question: the click is the answer, so there is nothing
    // left to confirm. This is what the TUI does.
    if (single) void submit(buildReply(items, updated))
  }

  const pickCustom = () => {
    setState(chooseCustom(state, step))
    setError(null)
    requestAnimationFrame(() => customRef.current?.focus())
  }

  const chosen = answerFor(state, step)
  const customChecked = state.customOn[step] === true

  return (
    <AgentDialog
      title={single ? current.header || "Question" : `Question ${step + 1} of ${items.length}`}
      headerActions={
        items.length > 1 && (
          <div className="flex shrink-0 items-center gap-1.5" role="group" aria-label="Questions">
            {items.map((item, index) => (
              <button
                key={index}
                type="button"
                disabled={busy}
                onClick={() => {
                  setError(null)
                  setStep(index)
                }}
                aria-label={`Question ${index + 1}: ${item.header}`}
                aria-current={index === step}
                title={item.header}
                className={`size-2 rounded-full transition-colors disabled:opacity-50 ${
                  index === step
                    ? "bg-accent"
                    : isAnswered(state, index)
                      ? "bg-accent-soft"
                      : "bg-border-strong"
                }`}
              />
            ))}
          </div>
        )
      }
      footer={
        single ? (
          <>
            <Button variant="ghost" onClick={() => void dismiss()} disabled={busy}>
              Dismiss
            </Button>
            {error && <p className="ml-auto text-sm text-danger">{error}</p>}
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => void dismiss()} disabled={busy}>
              Dismiss
            </Button>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="ml-auto flex items-center gap-2">
              {step > 0 && (
                <Button variant="subtle" onClick={back} disabled={busy}>
                  Back
                </Button>
              )}
              <Button onClick={next} disabled={busy}>
                {busy ? "Sending…" : last ? "Submit" : "Next"}
              </Button>
            </div>
          </>
        )
      }
    >
      <QuestionStep
        question={current}
        step={step}
        chosen={chosen}
        customChecked={customChecked}
        customText={state.custom[step] ?? ""}
        customRef={customRef}
        disabled={busy}
        onPick={pick}
        onPickCustom={pickCustom}
        onCustomText={(text) => setState(setCustom(state, step, text))}
      />
    </AgentDialog>
  )
}

function QuestionStep({
  question,
  step,
  chosen,
  customChecked,
  customText,
  customRef,
  disabled,
  onPick,
  onPickCustom,
  onCustomText,
}: {
  question: QuestionInfo
  step: number
  chosen: string[]
  customChecked: boolean
  customText: string
  customRef: React.RefObject<HTMLTextAreaElement | null>
  disabled: boolean
  onPick: (label: string) => void
  onPickCustom: () => void
  onCustomText: (text: string) => void
}) {
  const multiple = question.multiple === true
  // Checkboxes when several answers are allowed, radios when one is. The
  // protocol has carried `multiple` all along; this dialog only ever drew
  // radios, so a multi-select question could not be answered as asked.
  const type = multiple ? "checkbox" : "radio"
  const group = `question-${step}`

  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{question.header}</div>
      <p className="mt-1 text-sm text-foreground">{question.question}</p>
      {multiple && <p className="mt-1 text-xs text-muted">Choose as many as apply.</p>}

      <div className="mt-4 space-y-1">
        {question.options.map((option, index) => (
          <label
            key={index}
            className="flex cursor-pointer items-start gap-2.5 rounded border border-transparent p-2 hover:border-border hover:bg-surface-muted"
          >
            <input
              type={type}
              name={group}
              value={option.label}
              checked={chosen.includes(option.label)}
              disabled={disabled}
              onChange={() => onPick(option.label)}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm text-foreground">{option.label}</span>
              {option.description && <span className="block text-xs text-muted">{option.description}</span>}
            </span>
          </label>
        ))}

        {/* The agent's tool prompt tells the model not to add an "Other" option
            because the client is expected to provide one — so this is it, and
            it is absent only when the agent explicitly turns custom off. */}
        {allowsCustom(question) && (
          <label className="flex cursor-pointer items-start gap-2.5 rounded border border-transparent p-2 hover:border-border hover:bg-surface-muted">
            <input
              type={type}
              name={group}
              checked={customChecked}
              disabled={disabled}
              onChange={onPickCustom}
              className="mt-0.5"
            />
            <span className="text-sm text-foreground">{CUSTOM_LABEL}</span>
          </label>
        )}
      </div>

      {allowsCustom(question) && customChecked && (
        <textarea
          ref={customRef}
          value={customText}
          disabled={disabled}
          onChange={(e) => onCustomText(e.target.value)}
          rows={3}
          placeholder="Type your answer…"
          aria-label={`Your answer to: ${question.header}`}
          className="mt-2 w-full rounded border border-border bg-surface px-3 py-2 text-sm text-foreground"
        />
      )}
    </div>
  )
}
